import React from 'react';
import PhotoPermission, { usePhotoPermission } from './PhotoPermission';
import { API } from '../api';
import { Section, Toggle } from './ui';

/*
 * The club's gallery: every picture it has, in one place.
 *
 * There used to be three places. Reference photos for poster generation were
 * their own library, the club's logos lived in club settings, and newsletter
 * photographs were attached to each issue — so the same photograph had to be
 * uploaded three times, and each copy counted against a different limit.
 *
 * Everything the app draws on now comes from here. The posters studio picks
 * reference photos from it, the newsletter puts pictures in from it, and the
 * ones flagged as logos are what the logo pickers offer — because a list of
 * every photograph is not a list of logos.
 */

/* A picture shrunk before it leaves the browser. The server re-encodes it
   again on arrival, but a four-megabyte photograph has no business crossing
   the wire when what is stored is a fraction of that. */
export function fileToDataUrl(file, maxWidth = 2000) {
  return new Promise(resolve => {
    const fr = new FileReader();
    fr.onerror = () => resolve(null);
    fr.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(fr.result);
      img.onload = () => {
        try {
          /* A logo is kept as it arrives — club badges are usually PNGs with
             a transparent background, and a canvas would flatten that onto
             white and put a box behind the mark on every poster. */
          const scale = Math.min(1, maxWidth / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
          resolve(fr.result);
        }
      };
      img.src = fr.result;
    };
    fr.readAsDataURL(file);
  });
}

function GalleryTab({ clubId, showToast, showConfirm }) {
  const [photoOk, setPhotoOk] = usePhotoPermission();
  const [photos, setPhotos] = React.useState(null);
  const [posters, setPosters] = React.useState([]);
  const [usage, setUsage] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  /* Using a photo as a poster: which photo is choosing, the club's events
     and competitions to choose from (fetched the first time it is asked
     for), and the one picked — "e:12" for an event, "c:4" for a competition. */
  const [posterFor, setPosterFor] = React.useState(null);
  const [subjects, setSubjects] = React.useState(null);
  const [target, setTarget] = React.useState('');

  const qs = clubId ? '?clubId=' + clubId : '';

  const load = React.useCallback(async () => {
    setError('');
    const r = await fetch(`${API}/api/gallery${qs}`);
    if (!r.ok) { setError('Could not load the gallery.'); return; }
    const data = await r.json();
    setPhotos(data.photos);
    setPosters(data.posters || []);
    setUsage(data.usage);
  }, [qs]);

  React.useEffect(() => { load(); }, [load]);

  const add = async file => {
    const asLogo = false;
    if (!file) return undefined;
    setBusy(true);
    try {
      /* A logo goes up untouched so its transparency survives; a photograph
         is shrunk first because nothing needs it at phone-camera size. */
      const data = asLogo
        ? await new Promise(res => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.onerror = () => res(null);
          fr.readAsDataURL(file);
        })
        : await fileToDataUrl(file);
      if (!data) return showToast('That file could not be read', 'red');

      const r = await fetch(`${API}/api/gallery`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clubId, data, isLogo: asLogo,
          label: file.name.replace(/\.[a-z0-9]+$/i, '').slice(0, 100),
        }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) return showToast(out.error || 'That picture would not go in', 'red');
      await load();
      return showToast(asLogo ? 'Logo added' : 'Photo added', 'green');
    } finally { setBusy(false); }
  };

  const patch = async (photo, body) => {
    const r = await fetch(`${API}/api/gallery/${photo.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) return showToast('That change did not save', 'red');
    return load();
  };

  /**
   * Put a photograph on the club screen, or take it off.
   *
   * Its own function rather than another `patch` call because a switch has
   * to move the moment it is pressed — reloading the whole gallery before
   * anything changes on screen reads as a dead control — and because the
   * answer has to be checked rather than assumed. A server that predates
   * this feature accepts the request, ignores the field and returns 200, so
   * "it saved" and "it came back saved" are not the same thing: without the
   * check the switch would spring back with no explanation.
   */
  const setOnScreen = async (photo, next) => {
    const put = value => setPhotos(list =>
      list.map(x => (x.id === photo.id ? { ...x, onScreen: value } : x)));
    put(next);
    let saved = null;
    try {
      const r = await fetch(`${API}/api/gallery/${photo.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onScreen: next }),
      });
      saved = r.ok ? await r.json().catch(() => null) : null;
    } catch { saved = null; }

    if (!saved) {
      put(!next);
      return showToast('That change did not save', 'red');
    }
    if (!!saved.onScreen !== next) {
      put(!next);
      return showToast('The server did not take that — it needs the latest version deployed.', 'red');
    }
    put(!!saved.onScreen);
    return showToast(next ? 'On the club screen' : 'Taken off the club screen', 'green');
  };

  const startPosterFor = async photo => {
    setPosterFor(photo.id);
    setTarget('');
    if (subjects) return;
    const club = clubId ? 'clubId=' + clubId : '';
    const [er, cr] = await Promise.all([
      fetch(`${API}/api/events?all=1&${club}`).catch(() => null),
      fetch(`${API}/api/competitions?${club}`).catch(() => null),
    ]);
    const evList = er && er.ok ? await er.json().catch(() => []) : [];
    const compList = cr && cr.ok ? await cr.json().catch(() => []) : [];
    const byDate = d => (a, b) => String(d(a) || '9999').localeCompare(String(d(b) || '9999'));
    /* Nothing cancelled or finished — neither is worth a poster. Soonest
       first, undated last. */
    setSubjects({
      events: (Array.isArray(evList) ? evList : []).filter(ev => !ev.cancelled)
        .sort(byDate(ev => ev.nextDate || ev.startDate)),
      comps: (Array.isArray(compList) ? compList : []).filter(c => c.status !== 'completed')
        .sort(byDate(c => c.startDate)),
    });
  };

  /* The photo becomes a poster like a generated one — on, linked to the
     event or competition, and off the screens once it is over. */
  const attachPhotoAsPoster = async photo => {
    if (!target) return undefined;
    const [kind, id] = target.split(':');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/posters/from-photo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photoId: photo.id, [kind === 'c' ? 'competitionId' : 'eventId']: id }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) return showToast(out.error || 'That photo could not be made into a poster', 'red');
      setPosterFor(null);
      await load();
      const list = kind === 'c' ? subjects.comps : subjects.events;
      const hit = list.find(x => String(x.id) === String(id));
      return showToast(`Now the poster for ${hit ? hit.name : 'it'}`, 'green');
    } finally { setBusy(false); }
  };

  /* Ask what would notice before anything goes. A club at its limit still
     has to be able to delete something, so this warns rather than refuses. */
  const remove = async photo => {
    const r = await fetch(`${API}/api/gallery/${photo.id}/uses`);
    const uses = r.ok ? (await r.json()).uses : [];
    const message = uses.length
      ? `Delete “${photo.label}”?\n\nIt is used by:\n· ${uses.join('\n· ')}\n\nThis cannot be undone.`
      : `Delete “${photo.label}”?\n\nNothing is using it. This cannot be undone.`;
    if (!(await showConfirm(message))) return undefined;
    const d = await fetch(`${API}/api/gallery/${photo.id}`, { method: 'DELETE' });
    if (!d.ok) return showToast('It could not be deleted', 'red');
    await load();
    return showToast('Deleted', 'green');
  };

  /* Deleting a poster says what it would cost, because the two facts that
     matter are not obvious from a thumbnail: it is on the club screen right
     now, or it has already gone out in a newsletter and is in somebody's
     inbox pointing at it. */
  const removePoster = async poster => {
    const notes = [];
    if (poster.active) notes.push('It is on the club screen now.');
    if (poster.sharedAt) notes.push('It has been sent in a newsletter — that email will show a broken image.');
    const ok = await showConfirm(
      `Delete the poster “${poster.label}”?\n\n`
      + (notes.length ? notes.join('\n') + '\n\n' : '')
      + 'This cannot be undone, and it frees a poster slot.'
    );
    if (!ok) return undefined;
    const r = await fetch(`${API}/api/posters/${poster.id}`, { method: 'DELETE' });
    if (!r.ok) return showToast('It could not be deleted', 'red');
    await load();
    return showToast('Poster deleted', 'green');
  };

  if (!photos) {
    return (
      <div className="gs-panel">
        <Section label="Gallery">
          {error
            ? <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>
            : <p className="w3-text-grey">Loading…</p>}
        </Section>
      </div>
    );
  }

  /* One list, two kinds. A poster carries its kind so the grid can mark it
     and offer the right actions — a generated poster is not something you
     rename here or flag as a logo. */
  const asPoster = p => ({ ...p, kind: 'poster' });
  const shown = filter === 'logos' ? photos.filter(p => p.isLogo)
    : filter === 'photos' ? photos.filter(p => !p.isLogo)
      : filter === 'posters' ? posters.map(asPoster)
        : [...photos, ...posters.map(asPoster)]
          .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  const full = usage && usage.full;

  return (
    <div className="gs-panel">
      <Section label="Gallery">
        <p className="w3-text-grey gal-intro">
          Every picture the club has. Photos are uploaded here and used as reference for
          posters or put into the newsletter. Generated posters appear here too — they are
          made and edited under Posters. Logos are added under Club settings, with the
          club's colours, and show up here alongside everything else.
        </p>

        {full && (
          <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-orange">
            <p>
              This club is storing all {usage.cap} of the photos its plan allows. Delete
              some to make room, or ask about a bigger plan.
            </p>
          </div>
        )}

        <div className="gal-bar">
          <div className="gs-tabs">
            {[
              ['all', 'Everything', photos.length + posters.length],
              ['photos', 'Photos', photos.filter(p => !p.isLogo).length],
              ['logos', 'Logos', photos.filter(p => p.isLogo).length],
              ['posters', 'Posters', posters.length],
            ].map(([id, label, count]) => (
              <button key={id} className={`gs-tab ${filter === id ? 'gs-tab-on' : ''}`}
                onClick={() => setFilter(id)}>
                {label} ({count})
              </button>
            ))}
          </div>
          <div className="gal-adds">
            <label className={`w3-button w3-border${full || busy || !photoOk ? ' gal-disabled' : ''}`}
              title={photoOk ? undefined : 'Tick the box below first'}>
              <i className="fa fa-camera w3-margin-right"></i>Add a photo
              <input type="file" accept="image/*" disabled={full || busy || !photoOk}
                onChange={e => { add(e.target.files[0]); e.target.value = ''; }} />
            </label>

          </div>
        </div>
        <PhotoPermission ok={photoOk} onChange={setPhotoOk} id="gallery-photo-permission" />

        {shown.length === 0 ? (
          <p className="w3-text-grey">
            {filter === 'logos'
              ? 'No logos yet. Add one and it will be offered wherever a poster can carry a logo.'
              : filter === 'posters'
                ? 'No posters yet. They are made under Posters and appear here once they exist.'
                : 'Nothing here yet.'}
          </p>
        ) : (
          <div className="gal-grid">
            {shown.map(p => (p.kind === 'poster' ? (
              /* A generated poster. Shown here because it is one of the
                 club's pictures, but it is made rather than uploaded, so it
                 is not renamed or flagged from this screen — the poster
                 studio is where a poster is looked after. */
              <figure key={'poster' + p.id} className="gal-item is-poster">
                <div className="gal-thumb">
                  {p.thumb
                    ? <img src={p.thumb} alt={p.label} loading="lazy" />
                    : <span className="gal-nothumb">No preview</span>}
                  <span className="gal-badge is-poster">Poster</span>
                  {p.active && <span className="gal-badge is-on">On screen</span>}
                </div>
                <div className="gal-name">{p.label}</div>
                <div className="gal-meta">{p.size || ''}</div>
                <div className="gal-actions">
                  <span className="gal-hint">Edited under Posters</span>
                  <button className="w3-button w3-small w3-border" onClick={() => removePoster(p)}>
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              </figure>
            ) : (
              <figure key={p.id} className={`gal-item${p.isLogo ? ' is-logo' : ''}`}>
                <div className="gal-thumb">
                  <img src={p.thumb} alt={p.label} loading="lazy" />
                  {p.isLogo && <span className="gal-badge">Logo</span>}
                  {p.onScreen && <span className="gal-badge is-on">On screen</span>}
                </div>
                <input className="w3-input gal-label" value={p.label}
                  aria-label={`Name for ${p.label}`}
                  onChange={e => setPhotos(list =>
                    list.map(x => (x.id === p.id ? { ...x, label: e.target.value } : x)))}
                  onBlur={e => patch(p, { label: e.target.value })} />
                <div className="gal-meta">
                  {p.width ? `${p.width}×${p.height}` : ''}
                </div>
                <div className="gal-actions">
                  {p.isLogo
                    ? <span className="gal-hint">Managed under Club settings</span>
                    : (
                      /* Switching a photograph on for the TV, the same act as
                         switching a poster on — done here because this is
                         where the club looks at its pictures. A switch rather
                         than a button: it is a state the picture is in, and
                         it reads the same way as the slide switches on the
                         club screen page. A logo is not offered — a badge on
                         the big screen for 25 seconds is not a slide. */
                      <span className="gal-screen">
                        <Toggle
                          ariaLabel={`Show ${p.label} on the club screen`}
                          checked={!!p.onScreen}
                          onChange={next => setOnScreen(p, next)} />
                        <span className="gal-screen-label">On screen</span>
                      </span>
                    )}
                  {!p.isLogo && (
                    <button className="w3-button w3-small w3-border"
                      title="Use as the poster for an event or competition"
                      aria-label={`Use ${p.label} as a poster`}
                      onClick={() => (posterFor === p.id ? setPosterFor(null) : startPosterFor(p))}>
                      <i className="fa fa-image"></i>
                    </button>
                  )}
                  <button className="w3-button w3-small w3-border" onClick={() => remove(p)}>
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
                {posterFor === p.id && (
                  <div className="gal-poster-pick">
                    <label htmlFor={`gal-ev-${p.id}`} className="gal-meta">Poster for which event or competition?</label>
                    {subjects === null ? (
                      <div className="gal-meta">Loading…</div>
                    ) : subjects.events.length + subjects.comps.length === 0 ? (
                      <div className="gal-meta">No events or competitions yet.</div>
                    ) : (
                      <>
                        <select id={`gal-ev-${p.id}`} className="w3-select" value={target}
                          onChange={e => setTarget(e.target.value)}>
                          <option value="">Choose one</option>
                          {subjects.events.length > 0 && (
                            <optgroup label="Events">
                              {subjects.events.map(ev => (
                                <option key={'e' + ev.id} value={'e:' + ev.id}>
                                  {ev.name}{(ev.nextDate || ev.startDate) ? ` — ${String(ev.nextDate || ev.startDate).slice(0, 10)}` : ''}
                                  {ev.published ? '' : ' (not published)'}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {subjects.comps.length > 0 && (
                            <optgroup label="Competitions">
                              {subjects.comps.map(c => (
                                <option key={'c' + c.id} value={'c:' + c.id}>
                                  {c.name}{c.startDate ? ` — ${String(c.startDate).slice(0, 10)}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        <div className="gal-pick-actions">
                          <button className="w3-button w3-small poster-btn-primary w3-round"
                            disabled={!target || busy} onClick={() => attachPhotoAsPoster(p)}>
                            {busy ? 'Saving…' : 'Use as poster'}
                          </button>
                          <button className="w3-button w3-small w3-border w3-round" onClick={() => setPosterFor(null)}>
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </figure>
            )))}
          </div>
        )}
      </Section>
    </div>
  );
}

export default GalleryTab;
